import { Theme as MuiTheme } from "@mui/material/styles";
import { logger } from "@platform/browser-logger";
import { PolicyVariants } from "@platform/view-policy-components";
import { COVERAGE_NAMES } from "@platform/rfp-core";

import {
  ACCEPTED,
  ADMIN,
  ANALYST,
  BROKER_ANALYST,
  CONSULTANT,
  EXPIRED,
  MAX_ALLOWED_CHARACTER_FILE_NAME_LENGTH,
  PENDING,
} from "./constants";
import { CLIENT_FORM, DEFAULT_CLIENT_SINCE } from "./constants/client";

export {
  formatCurrency,
  formatCurrencyWithRound,
  formatPercent,
} from "@platform/fe-utils";

const { DENTAL } = COVERAGE_NAMES;

// Type definitions
type Theme = MuiTheme;

interface FileItem {
  fileName?: string;
  file?: { filename?: string };
  name?: string;
  [key: string]: unknown;
}

interface UploadedFile {
  fileName?: string;
  name?: string;
  documentTypes: Record<string, unknown>;
}

interface ClientProduct {
  lineOfCoverage?: string;
  annualizedPremium?: number;
  brokerCarrierInfo?: {
    carrier?: { name?: string };
    isStrategic?: boolean;
  };
  [key: string]: unknown;
}

interface ClientState {
  [key: string]: unknown;
}

interface BrokerClientInfoConfig {
  [key: string]: boolean | undefined;
}

interface ProductData {
  product: { name: string; [key: string]: unknown }[];
  [key: string]: unknown;
}

type Order = "asc" | "desc";
type Comparator<T> = (a: T, b: T) => number;

// The RFP wizard scrolls inside its own container rather than the window.
export const RFP_SCROLL_CONTAINER_ID = "rfp-scroll-container";

export function scrollToTop(): void {
  window.requestAnimationFrame(() =>
    document.getElementById(RFP_SCROLL_CONTAINER_ID)?.scrollTo({ top: 0 }),
  );
}

/* Re-exports from utils lib to avoid having to do a big refactor. */
export { extractErrorMessage } from "@platform/api";

export function descendingComparator<T>(a: T, b: T, orderBy: keyof T): number {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

export function getComparator<T>(
  order: Order,
  orderBy: keyof T,
): Comparator<T> {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

export function stableSort<T>(array: T[], comparator: Comparator<T>): T[] {
  const stabilizedThis: [T, number][] = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

export const getStatusColor = (theme: Theme, status: string): string => {
  let color = theme.palette.text.primary;

  if (status === ACCEPTED) {
    color = theme.palette.success.main;
  } else if (status === PENDING) {
    color = theme.palette.ui?.info ?? theme.palette.text.primary;
  } else if (status === EXPIRED) {
    color = theme.palette.ui?.error ?? theme.palette.text.primary;
  }

  return color;
};

function getDescendantProp<T>(obj: T, desc: string): unknown {
  const arr = desc.split(".");
  let current: unknown = obj;
  while (arr.length && current) {
    const key = arr.shift() as string;
    current = (current as Record<string, unknown>)[key];
  }
  return current;
}

export function sortDate<T>(data: T[], isAsc: boolean, orderBy: string): T[] {
  return data.sort((a, b) => {
    const aValue = Date.parse(getDescendantProp(a, orderBy) as string) || 0;
    const bValue = Date.parse(getDescendantProp(b, orderBy) as string) || 0;
    return isAsc ? aValue - bValue : bValue - aValue;
  });
}

export const mergeFileData = (
  uploadedFiles: FileItem[],
  additionalFiles: (FileItem | File)[],
): (FileItem | File)[] => {
  // Merging the arrays and removing duplicates based on unique file names
  const combined: (FileItem | File)[] = [];
  const uniqueNames = new Set<string>();

  for (const item of [...uploadedFiles, ...additionalFiles]) {
    // Determine the unique identifier, prioritize `fileName`, then `filename`, then `name`
    const fileItem = item as FileItem;
    const uniqueName =
      fileItem.fileName || fileItem.file?.filename || fileItem.name || "";

    // Add to combined if unique
    if (!uniqueNames.has(uniqueName)) {
      uniqueNames.add(uniqueName);
      if (item instanceof File) {
        combined.push(item);
      } else {
        combined.push({ ...fileItem, name: uniqueName });
      }
    }
  }
  return combined;
};

export function handleSort<T>(
  data: T[],
  order: Order,
  orderBy: string,
  objectType?: string | string[],
  onNull?: unknown,
): T[] {
  const dataCopy = data.slice();
  const isAsc = order === "asc";
  // ag-grid ColDef.type can be string | string[], normalize to string
  const type = Array.isArray(objectType) ? objectType[0] : objectType;
  if (type === "date" || type === "dateString") {
    return sortDate(data, isAsc, orderBy);
  }
  return dataCopy.sort((a, b) => {
    const aValue = getDescendantProp(a, orderBy) || onNull;
    const bValue = getDescendantProp(b, orderBy) || onNull;
    return aValue > bValue
      ? isAsc
        ? 1
        : -1
      : aValue < bValue
        ? isAsc
          ? -1
          : 1
        : 0;
  });
}

export const isValidEmail = (email: string): boolean =>
  /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email);

export const isUTC = (dateString?: string | null): boolean | undefined =>
  dateString?.endsWith("Z");

export const formatRevisedOrOriginalDate = (
  revisedDate?: string | null,
  originalDate?: string | null,
): string | null => {
  if (revisedDate) {
    return formatDate(revisedDate, isUTC(revisedDate)) || null;
  }
  if (originalDate) {
    return formatDate(originalDate, isUTC(originalDate)) || null;
  }
  return null;
};

export const formatDate = (
  date?: string | Date | null,
  isUTC = false,
  options?: Intl.DateTimeFormatOptions,
): string | undefined =>
  date
    ? new Date(date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        ...(isUTC && { timeZone: "UTC" }),
        ...options,
      })
    : undefined;

export const isValidDate = (
  dateString?: string | null,
  isUTC = false,
): boolean => {
  const formattedDate = formatDate(dateString, isUTC);
  const bottomLimit = new Date(1753, 0, 1);
  if (formattedDate) {
    const [, , year] = formattedDate.split("/");
    if (!!Date.parse(formattedDate) && /^\d{4}$/.test(year)) {
      const date = new Date(formattedDate);
      return date > bottomLimit;
    }
  }
  return false;
};

export function isValidAnnualPremium(value: unknown): boolean {
  if (value === "") return false;

  const strValue = String(value).trim();

  // Check that it's only digits
  if (!/^\d+$/.test(strValue)) return false;

  return !strValue.startsWith("0");
}

export function downloadURI(link: string, name: string): void {
  const a = document.createElement("a");
  a.style.display = "none";
  a.href = link;
  a.download = name;
  a.target = "_blank";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export function getJsonBlob(json: unknown): Blob {
  return new Blob([JSON.stringify(json)], {
    type: "text/json",
  });
}

export const formatBytes = (bytes: number, decimals = 2): string => {
  if (bytes === 0 || isNaN(bytes)) return "0 Bytes";
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
};

export const downloadBinaryFile = (
  binaryFile: BlobPart,
  fileName: string,
): void => {
  const blob = new Blob([binaryFile], { type: "application/octet-stream" });
  const url = URL.createObjectURL(blob);
  downloadURI(url, fileName);
  URL.revokeObjectURL(url);
};

export const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;
  const chunks: string[] = [];
  for (let i = 0; i < bytes.length; i += chunkSize) {
    chunks.push(String.fromCharCode(...bytes.subarray(i, i + chunkSize)));
  }
  return window.btoa(chunks.join(""));
};

export const base64ToArrayBuffer = (base64: string): ArrayBuffer => {
  const binary = window.atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
};

export const validateFileName = (fileName: string): void => {
  const regex = /["*/?<>:|]/;
  if (regex.test(fileName)) {
    throw new Error(`File names cannot contain special characters ("*/?<>:|).`);
  }
};

export const validateFileSize = (fileSize: number, maxSize: number): void => {
  if (fileSize > maxSize * 1024 * 1024) {
    throw new Error(`File size exceeds the maximum (${maxSize}MB).`);
  }
};

export const updateFileName = (originalFile: File, newName: string): File =>
  new File([originalFile], newName, {
    type: originalFile.type,
    lastModified: originalFile.lastModified,
  });

export const sanitizeFileNameLength = (fileName: string): string => {
  const lastDotIndex = fileName.lastIndexOf(".");
  const hasExtension = lastDotIndex !== -1;

  const namePart = hasExtension ? fileName.slice(0, lastDotIndex) : fileName;
  const extension = hasExtension ? fileName.slice(lastDotIndex) : "";

  // Remove special characters from the name part (keep alphanumeric, spaces, hyphens, underscores, dots)
  const sanitizedNamePart = namePart.replace(/[^a-zA-Z0-9 ._-]/g, "");
  const sanitizedFileName = sanitizedNamePart + extension;

  const encodedFullName = encodeURIComponent(sanitizedFileName);
  if (encodedFullName.length <= MAX_ALLOWED_CHARACTER_FILE_NAME_LENGTH) {
    return sanitizedFileName;
  }

  let trimmedNamePart = sanitizedNamePart;
  while (
    encodeURIComponent(trimmedNamePart + extension).length >
      MAX_ALLOWED_CHARACTER_FILE_NAME_LENGTH &&
    trimmedNamePart.length > 0
  ) {
    trimmedNamePart = trimmedNamePart.slice(0, -1);
  }

  const trimmedFileName = trimmedNamePart + extension;

  // Log only if encoded length was too long
  logger.error(
    `File name too long after encoding. Trimmed: "${fileName}" → "${trimmedFileName}"`,
    undefined,
    {
      originalFileName: fileName,
      trimmedFileName,
      encodedOriginalLength: encodedFullName.length,
      encodedTrimmedLength: encodeURIComponent(trimmedFileName).length,
    },
  );

  return trimmedFileName;
};

export const validateFileNameLength = (
  fileName: string,
  maxFileNameLength = MAX_ALLOWED_CHARACTER_FILE_NAME_LENGTH,
): void => {
  if (fileName.length > maxFileNameLength) {
    throw new Error(
      `The file ${fileName} cannot be uploaded because the file name is over the maximum of ${maxFileNameLength} characters. Please rename the file and try reuploading.`,
    );
  }
};

export const validateDuplicateFileName = (
  fileName: string,
  currentDocumentType: string,
  uploadedFiles: UploadedFile[] | undefined,
  files: FileItem[],
): void => {
  if (uploadedFiles?.length) {
    for (const item of uploadedFiles) {
      const documentTypes = Object.keys(item.documentTypes);

      if (item.fileName === fileName || item.name === fileName) {
        if (currentDocumentType === "Additional") {
          throw new Error(`additional-exist-in-other-documentType`);
        } else if (documentTypes.includes(currentDocumentType)) {
          throw new Error(`current-documentType-duplicate`);
        } else {
          throw new Error(`other-documentType-duplicate`);
        }
      }
    }
  }

  if (currentDocumentType === "Additional" && files.length) {
    for (const item of files) {
      if (item.fileName === fileName || item.name === fileName) {
        throw new Error(`current-documentType-duplicate`);
      }
    }
  }
};

export const validateFileMIMEType = (
  fileType: string,
  allowedTypes: string[],
): void => {
  if (!allowedTypes.includes(fileType)) {
    throw new Error(
      "File extension is not supported. (Allowed PDF, XLS, XLSM, XLSB, DOC or ZIP)",
    );
  }
};

export interface CoveragesData extends ClientProduct {
  coverage?: string;
  premium?: number;
  carrier?: string;
  isStrategic?: boolean;
}

export const normalizeClientProducts = (
  products: ClientProduct[] = [],
): CoveragesData[] =>
  products.reduce<CoveragesData[]>(
    (acc, p) => [
      ...acc,
      {
        ...p,
        coverage: p?.lineOfCoverage,
        premium: p?.annualizedPremium,
        carrier: p?.brokerCarrierInfo?.carrier?.name,
        isStrategic: p?.brokerCarrierInfo?.isStrategic,
      },
    ],
    [],
  );

export const addThousandsSeparator = (number?: number): string | undefined => {
  if (number) {
    return number.toLocaleString("en-US");
  }
  return undefined;
};

const {
  TOTAL_BENEFITS,
  NAME,
  CLIENT_SINCE,
  SIC,
  STREET_1,
  STREET_2,
  CITY,
  STATE,
  SITUS_STATE,
  ZIP_CODE,
  CLIENT_EXECUTIVE,
  PRACTICE,
  CORPORATE_ENTITY,
  ONEDIGITAL_OFFICE,
  OFFICE,
  PRODUCER_NAME,
  PRODUCER_FIRST_NAME,
  PRODUCER_LAST_NAME,
  PRODUCER_EMAIL,
  BROKER_TYPE,
} = CLIENT_FORM;

// eslint-disable-next-line complexity
export function getClientPayload(
  client: ClientState,
  brokerClientInfoConfig: BrokerClientInfoConfig,
): Record<string, unknown> {
  const clientExecutive =
    (brokerClientInfoConfig[CLIENT_EXECUTIVE] && client[CLIENT_EXECUTIVE]) ||
    null;
  const practice =
    (brokerClientInfoConfig[PRACTICE] &&
      (client[PRACTICE] as { value?: string })?.value) ||
    null;
  const corporateEntity =
    (brokerClientInfoConfig[CORPORATE_ENTITY] &&
      (client[CORPORATE_ENTITY] as { value?: string })?.value) ||
    null;
  const office =
    (brokerClientInfoConfig[OFFICE] &&
      (client[OFFICE] as { value?: string })?.value) ||
    null;
  const oneDigitalOffice =
    (brokerClientInfoConfig[ONEDIGITAL_OFFICE] && client[ONEDIGITAL_OFFICE]) ||
    null;

  return {
    [NAME]: client[NAME] || "",
    [BROKER_TYPE]: client[BROKER_TYPE] || "",
    [CLIENT_SINCE]: client[CLIENT_SINCE] || DEFAULT_CLIENT_SINCE,
    [SIC]: client[SIC] || "",
    [CITY]: client[CITY] || "",
    [STATE]: client[STATE],
    [ZIP_CODE]: client[ZIP_CODE] || "",
    [STREET_1]: client[STREET_1] || null,
    [STREET_2]: client[STREET_2] || null,
    [PRODUCER_NAME]: client[PRODUCER_NAME] || "",
    totalBenefitsEligibleEmployees: client[TOTAL_BENEFITS]
      ? parseInt(client[TOTAL_BENEFITS] as string, 10)
      : "",
    additionalInfo: {
      [CLIENT_EXECUTIVE]: clientExecutive,
      [PRACTICE]: practice,
      [CORPORATE_ENTITY]: corporateEntity,
      [OFFICE]: office,
      [ONEDIGITAL_OFFICE]: oneDigitalOffice,
    },
    [SITUS_STATE]: client[SITUS_STATE] || "",
    [PRODUCER_FIRST_NAME]: client[PRODUCER_FIRST_NAME] || "",
    [PRODUCER_LAST_NAME]: client[PRODUCER_LAST_NAME] || "",
    [PRODUCER_EMAIL]: client[PRODUCER_EMAIL] || "",
    hasInternationalEmployees:
      (client.hasInternationalEmployees as boolean | undefined) ?? false,
  };
}

export function mapLocToPolicyDetailsVariant(
  loc: string,
): PolicyVariants | null {
  const lower = loc.toLowerCase();
  switch (lower) {
    case "dental indemnity":
      return PolicyVariants.DENTAL_INDEMNITY;
    case "dental epo":
      return PolicyVariants.DENTAL_EPO;
    case "dental hmo":
      return PolicyVariants.DENTAL_HMO;
    case "dental pos":
      return PolicyVariants.DENTAL_POS;
    case "dental ppo":
      return PolicyVariants.DENTAL_PPO;
    case "vision discount only":
    case "vision dual option":
    case "vision fee schedule":
    case "vision ppo":
    case "vision single option":
      return PolicyVariants.VISION;
    case "long term disability":
      return PolicyVariants.LTD;
    case "short term disability":
      return PolicyVariants.STD;
    case "basic life/ad&d":
      return PolicyVariants.BASIC_LIFE;
    case "voluntary life/ad&d":
      return PolicyVariants.VOLUNTARY_LIFE;
    default:
      return null;
  }
}

export function filterProductDataBasedOnFlag(data: ProductData): ProductData {
  const product = data.product.filter((item) => item.name !== DENTAL);
  return { ...data, product };
}

export function getFullName({
  firstName,
  lastName,
}: {
  firstName?: string;
  lastName?: string;
}): string {
  return [firstName, lastName].filter(Boolean).join(" ");
}

export const parseIntSafe = (value: string | number): number => {
  if (typeof value === "number") return;
  if (!value) return 0;
  const numeric = Number(value.replace(/[^0-9.-]/g, ""));
  return Number.isFinite(numeric) ? Math.round(numeric) : 0;
};

// isInternalUser identifies Centro staff who bypass broker scoping.
// Use this to gate cross-broker access. Do NOT use to gate
// analyst-style RFP capabilities — see hasAnalystCapabilities.
// See ADR 10.19 for the capability/scope split.
export function isInternalUser(userType: unknown): boolean {
  return userType === ADMIN || userType === CONSULTANT || userType === ANALYST;
}

export function isAdminOrConsultant(userType: unknown): boolean {
  return userType === ADMIN || userType === CONSULTANT;
}

// hasAnalystCapabilities reports whether the user may exercise
// analyst-style RFP tooling (build, submit to carriers, record final
// decisions). Includes broker_analyst, which is broker-scoped — broker
// scoping is enforced server-side. See ADR 10.19.
export function hasAnalystCapabilities(userType: unknown): boolean {
  return (
    userType === ADMIN ||
    userType === ANALYST ||
    userType === CONSULTANT ||
    userType === BROKER_ANALYST
  );
}
